/**
 * SubscribeToWalletLiveChangesUseCase
 *
 * Starts a Firestore `onSnapshot` listener on the single document
 * `wallet/{userId}` and mirrors each non-empty snapshot into SecureStorage
 * under `SECURE_STORAGE_KEYS.WALLET`. Missing / empty documents are
 * ignored (matching Flutter's `if (walletInfo != null)` guard, which
 * leaves the local store untouched in that case).
 *
 * Why: wallet balance and payout fields can change server-side (purchase
 * crediting, withdrawal processing, admin adjustments). Without this
 * listener the web client would only see those updates after a full
 * resync — the SecureStorage listener (`WalletListener`) only fires on
 * local writes, so the Redux wallet slice and any UI built on it would
 * stay stale until the next app load. This use case closes the loop by
 * being the source of those local writes.
 *
 * Where it runs: started exactly once, at app boot, by `WalletListener`
 * in `presentation/components/app/` when the user is authenticated and
 * `firebaseUserId` is known. The component owns the returned
 * `Unsubscribe` and tears it down on sign-out / unmount. It is NOT
 * meant to be called from feature hooks or pages.
 *
 * Flutter parity: mirrors `WalletService.listenToWalletChanges`
 * (`lib/services/wallet_service.dart:13-29`), which Flutter starts at
 * app boot for the same reason.
 *
 * Architecture: thin wrapper over `WalletService.listenToWalletChanges`,
 * which holds the per-snapshot SecureStorage logic. Kept as a use case so
 * the presentation layer never reaches into a domain service directly,
 * matching `subscribeToPremiumPackagesLiveChangesUseCase`.
 */

import type { Unsubscribe } from 'firebase/firestore';
import { WalletService } from '@/domain/services/wallet/walletService';

export class SubscribeToWalletLiveChangesUseCase {
  constructor(private service: WalletService = WalletService.getInstance()) {}

  execute(userId: string): Unsubscribe {
    return this.service.listenToWalletChanges(userId);
  }
}

export const subscribeToWalletLiveChangesUseCase = new SubscribeToWalletLiveChangesUseCase();
